import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    List,
    ListItem,
    ListItemText,
    Paper,
    Radio,
    RadioGroup,
    TextField,
    Typography
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Display, FlightInfo, OrderInfo, OrderInfoType } from "adoms-common-lib";
import dayjs from "dayjs";
import 'dayjs/locale/ja';
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { APIConnector } from "../../connector/APIConnector";

type OrderTieFlightDialogProps = {
    isOrderTieFlightDialogOpen: boolean,
    setOrderTieFlightDialogOpen: React.Dispatch<React.SetStateAction<boolean>>,
    orderInfo: OrderInfo,
    setFlight: React.Dispatch<React.SetStateAction<FlightInfo>>
}

type AvailableFlightInfo = {
    flightInfo: FlightInfo,
    relatedOrders: OrderInfo[]
}

export const OrderTieFlightDialog: React.FC<OrderTieFlightDialogProps> = (props) => {
    const navigate = useNavigate();
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(dayjs().tz("Asia/Tokyo"));
    const [availableFlights, setAvailableFlights] = useState<AvailableFlightInfo[]>([]);
    const [selectedFlightId, setSelectedFlightId] = useState<string>();
    const [totalWeight, setTotalWeight] = useState<number>(0);
    const [isFlightListLoading, setIsFlightListLoading] = useState<boolean>(false);
    const [isPutFlightLoading, setIsPutFlightLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (props.isOrderTieFlightDialogOpen && selectedDate) {
            fetchAvailableFlights();
        }
    }, [selectedDate, props.isOrderTieFlightDialogOpen]);

    useEffect(() => {
        if (selectedFlightId) {
            calculateTotalWeight();
        } else {
            setTotalWeight(0);
        }
    }, [selectedFlightId]);

    const fetchAvailableFlights = async () => {
        setIsFlightListLoading(true);
        setErrorMessage(undefined);
        setAvailableFlights([]);
        setSelectedFlightId('');
        try {
            const dateString = Display.getDateStringFromISO8601(selectedDate.format());
            const apiConnector = APIConnector.instance;
            const result = await apiConnector.getFlightListByAvailableDeliveryAssign(dateString);

            // businessPartnerIdで絞り込み
            const filteredFlights = result.filter(
                item => {
                    return item.flightInfo.businessPartnerId === props.orderInfo.businessPartnerId &&
                        (item.flightInfo.departure.name === props.orderInfo.departure.name &&
                            item.flightInfo.arrival.name === props.orderInfo.arrival.name) ||
                        (item.flightInfo.departure.id === props.orderInfo.departure.id &&
                            item.flightInfo.arrival.id === props.orderInfo.arrival.id);
                }
            );
            setAvailableFlights(filteredFlights);
        } catch (error) {
            console.error(error);
            setErrorMessage("フライト情報の取得に失敗しました。");
        } finally {
            setIsFlightListLoading(false);
        }
    };

    const calculateTotalWeight = () => {
        const selectedFlightData = availableFlights.find(f => f.flightInfo.id === selectedFlightId);
        if (!selectedFlightData) return;

        const getOrderWeight = (order: OrderInfo): number => {
            if (order.type === OrderInfoType.MaritimeOrderInfo) {
                return order.goods.reduce((goodsSum, goodsInfo) => {
                    return goodsSum + (goodsInfo.weight * goodsInfo.count);
                }, 0);
            }

            // @ts-ignore
            return order.goods[0]?.weight ?? 0;
        };

        const currentOrderWeight = getOrderWeight(props.orderInfo);
        const relatedOrdersWeight = selectedFlightData.relatedOrders.reduce((sum, order) => {
            return sum + getOrderWeight(order);
        }, 0);

        setTotalWeight(currentOrderWeight + relatedOrdersWeight);
    };

    const handleDateChange = (newDate: dayjs.Dayjs | null) => {
        if (newDate) {
            setSelectedDate(newDate);
        }
    };

    const handleFlightSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedFlightId(event.target.value);
    };

    const handleConfirm = async () => {
        if (!selectedFlightId) {
            setErrorMessage("フライトを選択してください。");
            return;
        }
        setIsPutFlightLoading(true);
        setErrorMessage(undefined);
        try {
            const selectedFlightData = availableFlights.find(f => f.flightInfo.id === selectedFlightId);
            if (!selectedFlightData) throw new Error("選択されたフライトが見つかりません。");

            const newOrderIds = [...selectedFlightData.relatedOrders.map(o => o.orderID), props.orderInfo.orderID];

            const apiConnector = APIConnector.instance;
            const updatedFlight = await apiConnector.putFlightStatusChange(selectedFlightData.flightInfo, newOrderIds);

            navigate("/flightoperationview", {
                state: { id: updatedFlight.id },
            });
        } catch (error) {
            console.error(error);
            setErrorMessage("フライトの紐付けに失敗しました。");
        }
    };

    const handleClose = () => {
        props.setOrderTieFlightDialogOpen(false);
        setSelectedDate(dayjs().tz("Asia/Tokyo"));
        setAvailableFlights([]);
        setSelectedFlightId('');
        setTotalWeight(0);
        setErrorMessage(undefined);
    };

    return (
        <Dialog open={props.isOrderTieFlightDialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontWeight: "bold" }}>フライト選択</DialogTitle>
            <DialogContent>
                {isPutFlightLoading ? (
                    <Grid container justifyContent="center" alignItems="center" style={{ height: '100%' }}>
                        <CircularProgress />
                    </Grid>
                ) : errorMessage ? (<Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={5}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateCalendar value={selectedDate} onChange={handleDateChange} disablePast />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Typography variant="h6" gutterBottom>フライト一覧</Typography>
                            <Paper style={{ height: 300, overflow: 'auto' }}>
                                {isFlightListLoading ? (
                                    <Grid container justifyContent="center" alignItems="center" style={{ height: '100%' }}>
                                        <CircularProgress />
                                    </Grid>
                                ) : (
                                    <List>
                                        <RadioGroup
                                            value={selectedFlightId}
                                            onChange={handleFlightSelect}
                                        >
                                            {availableFlights.length > 0 ? availableFlights.map(({ flightInfo, relatedOrders }) => (
                                                <ListItem key={flightInfo.id} dense>
                                                    <FormControlLabel
                                                        value={flightInfo.id}
                                                        control={<Radio />}
                                                        label={
                                                            <ListItemText
                                                                primary={`フライトID: ${flightInfo.id}`}
                                                                secondary={`出発: ${flightInfo.std} 到着: ${flightInfo.sta} (${relatedOrders.length}件の配送依頼)`}
                                                            />
                                                        }
                                                    />
                                                </ListItem>
                                            )) : (
                                                <ListItem>
                                                    <ListItemText primary="利用可能なフライトはありません。" />
                                                </ListItem>
                                            )}
                                        </RadioGroup>
                                    </List>
                                )}
                            </Paper>
                            <Grid container spacing={2} sx={{ mt: 2 }} alignItems="center">
                                <Grid item>
                                    <Typography>紐付け後の合計重量:</Typography>
                                </Grid>
                                <Grid item>
                                    <TextField
                                        value={totalWeight > 0 ? `${totalWeight} g` : 'ー'}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                        error={totalWeight > 6712}
                                        helperText={totalWeight > 6712 ? "重量上限を超えています (6,712 g)" : ""}
                                        variant="standard"
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            {
                //TODO: 重量上限が7kgを超えたとき保存ボタンを押せないようにする
            }
            <DialogActions>
                {isPutFlightLoading ? null : (
                    <React.Fragment>
                        <Button onClick={handleClose}>キャンセル</Button>
                        <Button
                            onClick={handleConfirm}
                            variant="contained"
                            disabled={!selectedFlightId || isFlightListLoading || totalWeight > 7000}
                        >
                            決定
                        </Button>
                    </React.Fragment>
                )}
            </DialogActions>
        </Dialog>
    );
}
